// src/components/hooks/useWeeklySummary.js
import { useMemo } from 'react';
import moment from 'moment';

// ✅ Función auxiliar
const getFirstValidNumber = (...values) => {
  for (const val of values) {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
  }
  return 0;
};

const useWeeklySummary = (events = [], selectedWeek = null) => {
  return useMemo(() => {
    const summary = {
      planned: {
        totalActivities: 0,
        distance: 0,
        time: 0,
        calories: 0,
        elevation: 0,
        tss: 0,
        strengthSessions: 0,
      },
      completed: {
        totalActivities: 0,
        distance: 0,
        time: 0,
        calories: 0,
        elevation: 0,
        tss: 0,
        strengthSessions: 0,
      },
      byType: {}
    };

    const referenceDate = selectedWeek ? moment(selectedWeek) : moment();
    const startOfWeek = referenceDate.clone().startOf('isoWeek').startOf('day');
    const endOfWeek = referenceDate.clone().endOf('isoWeek').endOf('day');

    const validTypes = ['correr', 'ciclismo', 'nadar', 'fuerza'];
    validTypes.forEach(type => {
      summary.byType[type] = {
        planned: {
          totalActivities: 0,
          distance: 0,
          time: 0,
          calories: 0,
          tss: 0,
          elevation: 0,
        },
        completed: {
          totalActivities: 0,
          distance: 0,
          time: 0,
          calories: 0,
          tss: 0,
          elevation: 0,
        }
      };
    });

    events.forEach(event => {
      const eventDate = moment(event.start);
      if (!eventDate.isBetween(startOfWeek, endOfWeek, null, '[]')) return;

      const { type, planned = {}, completed = {}, metrics = {} } = event.extendedProps;
      const lowerType = (type || '').toLowerCase();

      if (!validTypes.includes(lowerType)) return;

      const group = summary.byType[lowerType];

      // ✅ Planificado
      const duracionMin = moment.duration(planned.duracion || '00:00:00').asMinutes();
      const dist = getFirstValidNumber(planned.distancia);
      const cal = getFirstValidNumber(planned.calorias);
      const elev = getFirstValidNumber(planned.desnivel_positivo);
      const tss = getFirstValidNumber(planned.tss);

      if (duracionMin || dist || cal || elev || tss) {
        summary.planned.totalActivities++;
        summary.planned.distance += dist;
        summary.planned.time += duracionMin;
        summary.planned.calories += cal;
        summary.planned.elevation += elev;
        summary.planned.tss += tss;
        if (lowerType === 'fuerza') summary.planned.strengthSessions++;

        group.planned.totalActivities++;
        group.planned.distance += dist;
        group.planned.time += duracionMin;
        group.planned.calories += cal;
        group.planned.elevation += elev;
        group.planned.tss += tss;
      }

      // ✅ Completado
      const fileId = completed?.fileId || completed?.fitFileId;
      if (fileId) {
        let duracionRaw = completed.duration || completed.duracion || completed.time || metrics.duracion || '00:00:00';
        let timeMin = 0;

        if (duracionRaw.includes(':')) {
          const [hh, mm, ss] = duracionRaw.split(':').map(Number);
          timeMin = hh * 60 + mm + ss / 60;
        } else {
          timeMin = moment.duration(Number(duracionRaw), 'seconds').asMinutes();
        }

        const distComp = getFirstValidNumber(completed.distance, metrics.distancia);
        const distanceKm = distComp > 1000 ? distComp / 1000 : distComp;

        const calComp = getFirstValidNumber(completed.calories, metrics.calorias);
        const elevComp = getFirstValidNumber(completed.elevation, completed.totalAscent, metrics.elevation);
        const tssComp = getFirstValidNumber(completed.tss, metrics.tss);

        summary.completed.totalActivities++;
        summary.completed.distance += distanceKm;
        summary.completed.time += timeMin;
        summary.completed.calories += calComp;
        summary.completed.elevation += elevComp;
        summary.completed.tss += tssComp;
        if (lowerType === 'fuerza') summary.completed.strengthSessions++;

        group.completed.totalActivities++;
        group.completed.distance += distanceKm;
        group.completed.time += timeMin;
        group.completed.calories += calComp;
        group.completed.elevation += elevComp;
        group.completed.tss += tssComp;
      }
    });

    return summary;
  }, [events, selectedWeek]);
};

export default useWeeklySummary;
